// frontend/src/components/dashboard/RoutePlanner.jsx
import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  MapPinIcon, 
  ClockIcon, 
  ArrowPathIcon,
  HandThumbUpIcon,
  HandThumbDownIcon
} from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';
import toast from 'react-hot-toast';

const RoutePlanner = ({ groupId, membership, user }) => {
  const [routePlans, setRoutePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  
  const [newRoute, setNewRoute] = useState({
    title: '',
    description: '',
    startPoint: '',
    destination: '',
    waypoints: [''],
    modeOfTransport: 'mixed'
  });
  
  useEffect(() => {
    loadRoutePlans();
  }, [groupId]);
  
  const loadRoutePlans = async () => {
    try {
      setLoading(true);
      const plans = await groupDashboardService.getRoutePlans(groupId);
      setRoutePlans(plans);
    } catch (error) {
      console.error('Failed to load route plans:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateRoute = async (e) => {
    e.preventDefault();
    try {
      await groupDashboardService.createRoutePlan(groupId, {
        ...newRoute,
        waypoints: newRoute.waypoints.filter(wp => wp.trim() !== '')
      });
      
      toast.success('Route plan created successfully');
      setNewRoute({
        title: '',
        description: '',
        startPoint: '',
        destination: '',
        waypoints: [''],
        modeOfTransport: 'mixed'
      });
      setShowCreateForm(false);
      loadRoutePlans();
    } catch (error) {
      console.error('Failed to create route:', error);
    }
  };
  
  const handleOptimizeRoute = async () => {
    if (!newRoute.startPoint || !newRoute.destination) {
      toast.error('Please enter start point and destination');
      return;
    }
    
    try {
      setOptimizing(true);
      const result = await groupDashboardService.optimizeRoute(groupId, {
        startPoint: newRoute.startPoint,
        destination: newRoute.destination,
        waypoints: newRoute.waypoints.filter(wp => wp.trim() !== '')
      });
      
      toast.success('Route optimized!');
      // Update form with optimized data
      setNewRoute(prev => ({
        ...prev,
        ...result.optimizedRoute
      }));
    } catch (error) {
      console.error('Failed to optimize route:', error);
    } finally {
      setOptimizing(false);
    }
  };
  
  const handleVote = async (planId, voteType) => {
    try {
      await groupDashboardService.voteForRoutePlan(groupId, planId, voteType);
      loadRoutePlans();
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };
  
  const addWaypoint = () => {
    setNewRoute({
      ...newRoute,
      waypoints: [...newRoute.waypoints, '']
    });
  };
  
  const updateWaypoint = (index, value) => {
    const newWaypoints = [...newRoute.waypoints];
    newWaypoints[index] = value;
    setNewRoute({ ...newRoute, waypoints: newWaypoints });
  };
  
  const removeWaypoint = (index) => {
    const newWaypoints = newRoute.waypoints.filter((_, i) => i !== index);
    setNewRoute({ ...newRoute, waypoints: newWaypoints });
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading route plans...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Route Planning</h2>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Route Plan
        </Button>
      </div>
      
      {/* Create Route Form */}
      {showCreateForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
          <h3 className="font-bold text-gray-800 mb-4 text-lg">Plan Your Route</h3>
          
          <form onSubmit={handleCreateRoute} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Route Title
                </label>
                <input
                  type="text"
                  value={newRoute.title}
                  onChange={(e) => setNewRoute({ ...newRoute, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Best Route to Mountains"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transport Mode
                </label>
                <select
                  value={newRoute.modeOfTransport}
                  onChange={(e) => setNewRoute({ ...newRoute, modeOfTransport: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="driving">🚗 Driving</option>
                  <option value="walking">🚶 Walking</option>
                  <option value="cycling">🚴 Cycling</option>
                  <option value="public_transport">🚌 Public Transport</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Point
                </label>
                <input
                  type="text"
                  value={newRoute.startPoint}
                  onChange={(e) => setNewRoute({ ...newRoute, startPoint: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Mumbai Central"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destination
                </label>
                <input
                  type="text"
                  value={newRoute.destination}
                  onChange={(e) => setNewRoute({ ...newRoute, destination: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Goa Beach"
                  required
                />
              </div>
            </div>
            
            {/* Waypoints */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Waypoints (Optional)
                </label>
                <button
                  type="button"
                  onClick={addWaypoint}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Waypoint
                </button>
              </div>
              
              {newRoute.waypoints.map((waypoint, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={waypoint}
                    onChange={(e) => updateWaypoint(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Stop ${index + 1}`}
                  />
                  {newRoute.waypoints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeWaypoint(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={newRoute.description}
                onChange={(e) => setNewRoute({ ...newRoute, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Describe your route plan..."
              />
            </div>
            
            <div className="flex justify-between items-center pt-4">
              <Button
                type="button"
                onClick={handleOptimizeRoute}
                disabled={optimizing}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg"
              >
                <ArrowPathIcon className={`h-5 w-5 mr-2 ${optimizing ? 'animate-spin' : ''}`} />
                {optimizing ? 'Optimizing...' : 'Optimize Route'}
              </Button>
              
              <div className="flex space-x-3">
                <Button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg"
                >
                  Create Route Plan
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
      
      {/* Route Plans List */}
      <div className="space-y-6">
        {routePlans.length === 0 ? (
          <div className="text-center py-12">
            <MapPinIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Route Plans Yet</h3>
            <p className="text-gray-600 mb-6">Create your first route plan to get started!</p>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg"
            >
              Create First Route
            </Button>
          </div>
        ) : (
          routePlans.map((plan) => (
            <div key={plan._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{plan.title}</h3>
                  {plan.description && (
                    <p className="text-gray-600 mt-1">{plan.description}</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-sm text-gray-500">Votes</div>
                    <div className="text-2xl font-bold text-blue-600">{plan.totalVotes}</div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleVote(plan._id, 'upvote')}
                      className={`p-2 rounded-full ${
                        plan.votes?.some(v => v.userId._id === user._id && v.type === 'upvote')
                          ? 'bg-green-100 text-green-600'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      <HandThumbUpIcon  className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleVote(plan._id, 'downvote')}
                      className={`p-2 rounded-full ${
                        plan.votes?.some(v => v.userId._id === user._id && v.type === 'downvote')
                          ? 'bg-red-100 text-red-600'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      <HandThumbDownIcon  className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                  <MapPinIcon className="h-5 w-5 text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Transport</p>
                    <p className="font-medium capitalize">{plan.modeOfTransport.replace('_', ' ')}</p>
                  </div>
                </div>
                
                {plan.totalDistance && (
                  <div className="flex items-center p-3 bg-green-50 rounded-lg">
                    <MapPinIcon className="h-5 w-5 text-green-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Distance</p>
                      <p className="font-medium">{plan.totalDistance}</p>
                    </div>
                  </div>
                )}
                
                {plan.estimatedTime && (
                  <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-purple-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Estimated Time</p>
                      <p className="font-medium">{plan.estimatedTime}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {plan.waypoints && plan.waypoints.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Route Stops:</h4>
                  <div className="flex flex-wrap gap-2">
                    {plan.waypoints.map((wp, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {wp.name || wp}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-2">
                    {plan.createdBy?.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{plan.createdBy?.name}</p>
                    <p className="text-xs text-gray-500">
                      Created {new Date(plan.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500">
                  {plan.votes?.length || 0} members voted
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RoutePlanner;